import React, { ForwardedRef, forwardRef, memo, useCallback, useMemo, useRef, useState } from 'react'
import { trackError } from 'services/analytics'
import { optimizeSrc } from 'services/filestack'
import usePreloadImage from 'hooks/usePreloadImage'

export interface IPictureProps extends Omit<React.ImgHTMLAttributes<HTMLImageElement>, 'onError'> {
  src: string
  srcFallback?: string
  alt?: string
  width?: number
  preload?: boolean
  onError?: (error: Error) => void
}

// TODO: figure out if we can use next/image instead with custom loaders

function Picture({ onError, src, srcFallback, alt, width, style, preload = false, ...props }: IPictureProps, ref: ForwardedRef<HTMLImageElement>) {
  const [srcEmpty, setSrcEmpty] = useState(false)
  const [retryCount, setRetryCount] = useState(0)
  const timeout = useRef<number>(null)

  const retryLoadingImage = useCallback(() => {
    setSrcEmpty(true)
    timeout.current = window.setTimeout(() => {
      timeout.current = null
      setSrcEmpty(false)
      setRetryCount(value => value + 1)
    }, 300)
  }, [timeout.current])

  const handleError = useCallback(() => {
    if (!srcEmpty) {
      const err = new Error('Failed to load image')

      if (retryCount < 2) {
        retryLoadingImage()
      } else {
        trackError(err, 'medium', {
          src,
          srcFallback,
        })
        if (onError) onError(err)
      }
    }
  }, [onError, src, srcFallback, retryLoadingImage])

  const effectiveSrc = srcEmpty ? undefined : src
  const effectiveSrcFallback = srcEmpty ? undefined : srcFallback

  const [srcProcessed, imageType] = useMemo(() => {
    let optimizedSrc = effectiveSrc
    if (effectiveSrc) {
      optimizedSrc = optimizeSrc(effectiveSrc, { width })
    }

    if (!optimizedSrc) {
      return [null, null]
    }

    const extension = optimizedSrc.slice(optimizedSrc.lastIndexOf('.') + 1)

    let detectedImageType = null
    if (optimizedSrc.includes('format:')) {
      // This detects format out of optimized filestack URLs
      const format = optimizedSrc.match(/format:(\w+)/)?.[1]
      detectedImageType = format ? `image/${format}` : null
    } else if (optimizedSrc.indexOf('data:image') === 0) {
      detectedImageType = optimizedSrc.slice('data:'.length, optimizedSrc.indexOf(';'))
    } else if (extension === 'jpg') {
      detectedImageType = 'image/jpeg'
    } else {
      detectedImageType = `image/${extension}`
    }

    return [optimizedSrc, detectedImageType]
  }, [effectiveSrc, width])

  usePreloadImage(preload ? srcProcessed : undefined)
  usePreloadImage(preload ? effectiveSrcFallback : undefined)

  // NOTE: sometimes the srcProcessed does not load correctly if the image cannot be read
  // by Filestack's optimizer API, so it is useful to use the non-processed src as a fallback
  if (effectiveSrcFallback || (srcProcessed !== effectiveSrc)) {
    return (
      <picture>
        <source srcSet={srcProcessed} type={imageType} />
        <img
          src={effectiveSrcFallback || effectiveSrc}
          alt={alt}
          style={{ width: width, ...style }}
          ref={ref}
          {...props}
          onError={handleError}
        />
      </picture>
    )
  }

  if (imageType === 'image/webp') {
    console.warn(`Picture with src ${src}: no fallback image provided!`)
  }

  return (
    // eslint-disable-next-line @next/next/no-img-element
    <img
      src={srcProcessed}
      alt={alt}
      style={{ width: width, ...style }}
      ref={ref}
      {...props}
      onError={handleError}
    />
  )
}

export default memo(forwardRef(Picture))
